import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  api,
  type EnvironmentInput,
  type EnvironmentKind,
  type EnvironmentSnapshot,
  type ProjectEnvironment,
} from '../api';
import { useAuth } from '../auth';
import { Button, Page } from '../ui';

interface InspectState {
  loading: boolean;
  snapshot: EnvironmentSnapshot | null;
  error: string | null;
}

export function Environments() {
  const { slug } = useParams<{ slug: string }>();
  const { me } = useAuth();
  const [envs, setEnvs] = useState<ProjectEnvironment[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [snaps, setSnaps] = useState<Record<string, InspectState>>({});

  const isAdmin = me?.globalRole === 'admin';

  const load = () => {
    if (!slug) return;
    api
      .listEnvironments(slug)
      .then(({ environments }) => setEnvs(environments))
      .catch((e) => setErr(e.status === 403 ? 'Sem permissão' : 'Falha ao carregar'));
  };

  useEffect(load, [slug]);

  async function runInspect(env: ProjectEnvironment) {
    if (!slug) return;
    setSnaps((s) => ({ ...s, [env.id]: { loading: true, snapshot: null, error: null } }));
    try {
      const { snapshot } = await api.inspectEnvironment(slug, env.id);
      setSnaps((s) => ({ ...s, [env.id]: { loading: false, snapshot, error: null } }));
    } catch (e) {
      const err = e as { error?: string; status?: number };
      setSnaps((s) => ({
        ...s,
        [env.id]: { loading: false, snapshot: null, error: err.error ?? `Falha (${err.status ?? '?'})` },
      }));
    }
  }

  async function remove(env: ProjectEnvironment) {
    if (!slug) return;
    if (!confirm(`Remover ambiente "${env.name}"?`)) return;
    await api.deleteEnvironment(slug, env.id);
    load();
  }

  if (!slug) return null;

  return (
    <Page
      title="Ambientes"
      subtitle="Onde rodam os testes E2E e onde o orchestrator dispara deploys."
      actions={
        isAdmin && editing !== 'new' ? (
          <Button variant="primary" onClick={() => setEditing('new')}>
            + Novo ambiente
          </Button>
        ) : undefined
      }
    >
      {err && <div className="ui-error-banner" style={{ marginBottom: 12 }}>{err}</div>}

      {editing === 'new' && (
        <EnvironmentForm
          slug={slug}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      <div className="env-list">
        {envs.length === 0 && editing !== 'new' && (
          <div className="env-empty">
            Nenhum ambiente cadastrado ainda.
            {isAdmin ? ' Clique em "novo ambiente" pra registrar a VPS ou outro host.' : ''}
          </div>
        )}
        {envs.map((env) => (
          <EnvironmentCard
            key={env.id}
            env={env}
            slug={slug}
            editing={editing === env.id}
            snap={snaps[env.id]}
            isAdmin={isAdmin}
            onEdit={() => setEditing(env.id)}
            onCancelEdit={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
            }}
            onInspect={() => runInspect(env)}
            onDelete={() => remove(env)}
          />
        ))}
      </div>
    </Page>
  );
}

function EnvironmentCard({
  env,
  slug,
  editing,
  snap,
  isAdmin,
  onEdit,
  onCancelEdit,
  onSaved,
  onInspect,
  onDelete,
}: {
  env: ProjectEnvironment;
  slug: string;
  editing: boolean;
  snap: InspectState | undefined;
  isAdmin: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaved: () => void;
  onInspect: () => void;
  onDelete: () => void;
}) {
  const [rolesBusy, setRolesBusy] = useState(false);
  async function setupRoles() {
    if (!env.dbContainer || !env.dbName) {
      alert('Configure o container e o nome do banco em "editar" primeiro.');
      return;
    }
    const superuser = window.prompt('Role superusuário do Postgres para criar as roles (ex: postgres; no mistik = mistikisland):', 'postgres');
    if (!superuser) return;
    const createWrite = window.confirm('Criar também a role de ESCRITA (bp_agent_rw)?\nOK = sim · Cancelar = só leitura.');
    setRolesBusy(true);
    try {
      const r = await api.setupEnvRoles(slug, env.id, { superuser: superuser.trim(), createWrite });
      alert('Roles provisionadas ✅\n' + r.output);
      onSaved();
    } catch (e) {
      const err = e as { error?: string; detail?: string };
      alert('Falha ao provisionar: ' + (err.detail ?? err.error ?? 'erro'));
    } finally {
      setRolesBusy(false);
    }
  }
  return (
    <section className="env-card">
      <div className="env-card-head">
        <div>
          <h2>
            {env.name}
            <span className={`env-badge env-kind-${env.kind}`}>{env.kind}</span>
            {!env.active && <span className="env-badge env-inactive">inativo</span>}
          </h2>
          <div className="env-target">
            {env.kind === 'local' ? (
              <span className="env-meta">host local (API)</span>
            ) : (
              <span className="env-meta">
                {env.sshUser ?? '—'}@{env.host ?? '—'}
                {env.sshPort ? `:${env.sshPort}` : ''}
              </span>
            )}
            {env.url && <span className="env-meta">🔗 <a href={env.url} target="_blank" rel="noreferrer">{env.url}</a></span>}
            {env.composeFile && <span className="env-meta">compose: <code>{env.composeFile}</code></span>}
          </div>
        </div>
        <div className="env-actions">
          <button type="button" onClick={onInspect} disabled={snap?.loading}>
            {snap?.loading ? 'inspecionando…' : 'inspecionar'}
          </button>
          {isAdmin && !editing && (
            <>
              <button type="button" className="link" onClick={onEdit}>editar</button>
              {env.kind === 'vps' && (
                <button type="button" className="link" onClick={setupRoles} disabled={rolesBusy} title="Cria bp_agent_ro/bp_agent_rw no banco deste ambiente">
                  {rolesBusy ? 'provisionando…' : 'provisionar roles'}
                </button>
              )}
              <button type="button" className="link danger" onClick={onDelete}>remover</button>
            </>
          )}
        </div>
      </div>

      {editing && (
        <EnvironmentForm
          slug={slug}
          env={env}
          onCancel={onCancelEdit}
          onSaved={onSaved}
        />
      )}

      {env.notes && <div className="env-notes">{env.notes}</div>}

      {snap?.error && <div className="error">Erro: {snap.error}</div>}
      {snap?.snapshot && <SnapshotView snap={snap.snapshot} />}
    </section>
  );
}

function SnapshotView({ snap }: { snap: EnvironmentSnapshot }) {
  return (
    <div className="env-snap">
      <div className="env-snap-head">
        <span className="env-meta">
          Coletado {new Date(snap.ranAt).toLocaleTimeString()} · {snap.durationMs}ms
          {!snap.ok && ' · com erro'}
        </span>
      </div>

      {snap.error && <div className="error">Inspeção falhou: {snap.error}</div>}

      <div className="env-snap-grid">
        <div className="env-snap-tile">
          <h3>Host</h3>
          <dl className="kv">
            <dt>hostname</dt><dd>{snap.host.hostname ?? '—'}</dd>
            <dt>kernel</dt><dd>{snap.host.kernel ?? '—'}</dd>
            <dt>uptime</dt><dd>{snap.host.uptime ?? '—'}</dd>
            <dt>cpu</dt>
            <dd>
              {snap.host.cpu?.model ?? '—'}
              {snap.host.cpu?.cores ? ` · ${snap.host.cpu.cores} cores` : ''}
            </dd>
            <dt>loadavg</dt>
            <dd>
              {snap.host.loadavg ?? '—'}
              {snap.host.loadavg && snap.host.cpu?.cores ? (
                <LoadSaturation loadavg={snap.host.loadavg} cores={snap.host.cpu.cores} />
              ) : null}
            </dd>
          </dl>
        </div>

        <div className="env-snap-tile">
          <h3>Memória</h3>
          {snap.memory ? (
            <>
              <MemoryBar mem={snap.memory} />
              <dl className="kv">
                <dt>total</dt><dd>{snap.memory.totalMb} MB</dd>
                <dt>usada</dt><dd>{snap.memory.usedMb} MB</dd>
                <dt>livre</dt><dd>{snap.memory.freeMb} MB</dd>
                {snap.memory.availableMb !== null && (
                  <><dt>disponível</dt><dd>{snap.memory.availableMb} MB</dd></>
                )}
              </dl>
            </>
          ) : (
            <div className="env-meta">sem dados</div>
          )}
        </div>

        <div className="env-snap-tile env-snap-wide">
          <h3>Discos</h3>
          {snap.disks.length === 0 ? (
            <div className="env-meta">sem dados</div>
          ) : (
            <table className="env-table">
              <thead><tr><th>montagem</th><th>total</th><th>usado</th><th>livre</th><th>%</th><th>fs</th></tr></thead>
              <tbody>
                {snap.disks.map((d, i) => (
                  <tr key={i}>
                    <td>{d.mount}</td><td>{d.size}</td><td>{d.used}</td><td>{d.avail}</td>
                    <td><DiskPct pct={d.usePct} /></td>
                    <td><code>{d.filesystem}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="env-snap-tile env-snap-wide">
          <h3>
            Docker
            {snap.docker.installed ? (
              <span className="env-meta"> · {snap.docker.version}</span>
            ) : (
              <span className="env-meta"> · não instalado</span>
            )}
          </h3>
          {snap.containers.length === 0 ? (
            <div className="env-meta">
              {snap.docker.installed ? 'nenhum container rodando' : '—'}
            </div>
          ) : (
            <table className="env-table">
              <thead>
                <tr>
                  <th>container</th>
                  <th>imagem</th>
                  <th>status</th>
                  <th>CPU</th>
                  <th>memória</th>
                  <th>net I/O</th>
                  <th>block I/O</th>
                  <th>portas</th>
                </tr>
              </thead>
              <tbody>
                {snap.containers.map((c, i) => (
                  <tr key={i}>
                    <td><code>{c.name}</code></td>
                    <td><code>{c.image}</code></td>
                    <td>{c.status}</td>
                    <td><PctCell value={c.cpuPerc} /></td>
                    <td>
                      <PctCell value={c.memPerc} />
                      {c.memUsage && <div className="env-meta-inline">{c.memUsage}</div>}
                    </td>
                    <td className="env-meta-inline">{c.netIO ?? '—'}</td>
                    <td className="env-meta-inline">{c.blockIO ?? '—'}</td>
                    <td className="env-ports">{c.ports || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {snap.compose.file && (
          <div className="env-snap-tile env-snap-wide">
            <h3>Compose <span className="env-meta"> · <code>{snap.compose.file}</code></span></h3>
            {snap.compose.services.length === 0 ? (
              <div className="env-meta">nenhum serviço encontrado no compose</div>
            ) : (
              <table className="env-table">
                <thead><tr><th>serviço</th><th>container</th><th>estado</th><th>status</th></tr></thead>
                <tbody>
                  {snap.compose.services.map((s, i) => (
                    <tr key={i}>
                      <td>{s.service}</td>
                      <td><code>{s.name}</code></td>
                      <td>{s.state}</td>
                      <td>{s.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {snap.raw && (
          <div className="env-snap-tile env-snap-wide">
            <details>
              <summary>saída bruta (debug)</summary>
              <pre>{snap.raw}</pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

function MemoryBar({ mem }: { mem: NonNullable<EnvironmentSnapshot['memory']> }) {
  const pct = useMemo(() => {
    if (!mem.totalMb) return 0;
    const used = mem.availableMb !== null ? mem.totalMb - mem.availableMb : mem.usedMb;
    return Math.max(0, Math.min(100, Math.round((used / mem.totalMb) * 100)));
  }, [mem]);
  return (
    <div className="env-bar" title={`${pct}% em uso`}>
      <div className={`env-bar-fill env-bar-${bucket(pct)}`} style={{ width: `${pct}%` }} />
      <span className="env-bar-label">{pct}%</span>
    </div>
  );
}

function DiskPct({ pct }: { pct: string }) {
  const n = Number(pct.replace('%', ''));
  if (Number.isNaN(n)) return <span>{pct}</span>;
  return (
    <div className="env-bar env-bar-inline" title={pct}>
      <div className={`env-bar-fill env-bar-${bucket(n)}`} style={{ width: `${n}%` }} />
      <span className="env-bar-label">{n}%</span>
    </div>
  );
}

function bucket(pct: number): 'ok' | 'warn' | 'crit' {
  if (pct >= 90) return 'crit';
  if (pct >= 70) return 'warn';
  return 'ok';
}

function PctCell({ value }: { value: string | null }) {
  if (!value) return <span className="env-meta">—</span>;
  const n = Number(value.replace('%', ''));
  if (Number.isNaN(n)) return <span>{value}</span>;
  return (
    <div className="env-bar env-bar-inline" title={value}>
      <div className={`env-bar-fill env-bar-${bucket(n)}`} style={{ width: `${Math.min(100, n)}%` }} />
      <span className="env-bar-label">{value}</span>
    </div>
  );
}

function LoadSaturation({ loadavg, cores }: { loadavg: string; cores: number }) {
  // loadavg = "0.20 0.23 0.20 2/576 2296342" → usa o 1-min
  const first = Number(loadavg.trim().split(/\s+/)[0]);
  if (Number.isNaN(first) || cores <= 0) return null;
  const pct = Math.round((first / cores) * 100);
  return (
    <div className="env-bar env-bar-inline" title={`load/core 1m: ${pct}%`}>
      <div className={`env-bar-fill env-bar-${bucket(pct)}`} style={{ width: `${Math.min(100, pct)}%` }} />
      <span className="env-bar-label">{pct}%</span>
    </div>
  );
}

function EnvironmentForm({
  slug,
  env,
  onCancel,
  onSaved,
}: {
  slug: string;
  env?: ProjectEnvironment;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(env?.name ?? '');
  const [kind, setKind] = useState<EnvironmentKind>(env?.kind ?? 'vps');
  const [url, setUrl] = useState(env?.url ?? '');
  const [host, setHost] = useState(env?.host ?? '');
  const [sshUser, setSshUser] = useState(env?.sshUser ?? 'root');
  const [sshPort, setSshPort] = useState(env?.sshPort?.toString() ?? '22');
  const [sshKeyPath, setSshKeyPath] = useState(env?.sshKeyPath ?? '');
  const [composeFile, setComposeFile] = useState(env?.composeFile ?? '');
  const [composeProject, setComposeProject] = useState(env?.composeProject ?? '');
  const [notes, setNotes] = useState(env?.notes ?? '');
  const [tier, setTier] = useState(env?.tier ?? 'other');
  const [dbContainer, setDbContainer] = useState(env?.dbContainer ?? '');
  const [dbName, setDbName] = useState(env?.dbName ?? '');
  const [dbRoleRead, setDbRoleRead] = useState(env?.dbRoleRead ?? '');
  const [dbRoleWrite, setDbRoleWrite] = useState(env?.dbRoleWrite ?? '');
  const [active, setActive] = useState(env?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const payload: EnvironmentInput = {
      name: name.trim(),
      kind,
      active,
    };
    if (kind === 'vps') {
      payload.host = host.trim();
      payload.sshUser = sshUser.trim();
      const portN = Number(sshPort);
      if (!Number.isNaN(portN) && portN > 0) payload.sshPort = portN;
      if (sshKeyPath.trim()) payload.sshKeyPath = sshKeyPath.trim();
    }
    if (composeFile.trim()) payload.composeFile = composeFile.trim();
    if (composeProject.trim()) payload.composeProject = composeProject.trim();
    if (notes.trim()) payload.notes = notes.trim();
    payload.url = url.trim();
    payload.tier = tier;
    payload.dbContainer = dbContainer.trim();
    payload.dbName = dbName.trim();
    payload.dbRoleRead = dbRoleRead.trim();
    payload.dbRoleWrite = dbRoleWrite.trim();

    try {
      if (env) await api.updateEnvironment(slug, env.id, payload);
      else await api.createEnvironment(slug, payload);
      onSaved();
    } catch (e) {
      const err = e as { error?: string; status?: number };
      setMsg(err.error ?? `Falha (${err.status ?? '?'})`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="env-form" onSubmit={submit}>
      <div className="env-form-row">
        <label>
          <span>nome</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="prod / dev / staging" />
        </label>
        <label>
          <span>tipo</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as EnvironmentKind)}>
            <option value="vps">VPS (SSH)</option>
            <option value="local">local (host da API)</option>
          </select>
        </label>
      </div>

      <label>
        <span>URL da aplicação</span>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://dev.mistik.island" />
      </label>

      {kind === 'vps' && (
        <>
          <div className="env-form-row">
            <label>
              <span>host / IP</span>
              <input value={host} onChange={(e) => setHost(e.target.value)} required placeholder="84.46.248.217" />
            </label>
            <label>
              <span>porta SSH</span>
              <input value={sshPort} onChange={(e) => setSshPort(e.target.value)} placeholder="22" />
            </label>
          </div>
          <div className="env-form-row">
            <label>
              <span>usuário SSH</span>
              <input value={sshUser} onChange={(e) => setSshUser(e.target.value)} required placeholder="root" />
            </label>
            <label>
              <span>caminho da chave SSH (no host da API)</span>
              <input
                value={sshKeyPath}
                onChange={(e) => setSshKeyPath(e.target.value)}
                placeholder="/root/.ssh/id_ed25519 ou ~/.ssh/mistik-vps"
              />
            </label>
          </div>
        </>
      )}

      <div className="env-form-row">
        <label>
          <span>docker-compose file (opcional)</span>
          <input
            value={composeFile}
            onChange={(e) => setComposeFile(e.target.value)}
            placeholder="/opt/mistik/docker-compose.dev.yml"
          />
        </label>
        <label>
          <span>compose project (opcional, -p)</span>
          <input
            value={composeProject}
            onChange={(e) => setComposeProject(e.target.value)}
            placeholder="mistik-dev"
          />
        </label>
      </div>

      <div className="env-form-row">
        <label>
          <span>tier (classificação)</span>
          <select value={tier} onChange={(e) => setTier(e.target.value)}>
            <option value="dev">dev / develop</option>
            <option value="test">test</option>
            <option value="hml">hml / staging</option>
            <option value="prod">prod</option>
            <option value="ephemeral">ephemeral</option>
            <option value="other">outro</option>
          </select>
        </label>
        <label>
          <span>container do Postgres (acesso dos agentes)</span>
          <input value={dbContainer} onChange={(e) => setDbContainer(e.target.value)} placeholder="mistik-shared-postgres-1" />
        </label>
      </div>
      <div className="env-form-row">
        <label>
          <span>nome do banco</span>
          <input value={dbName} onChange={(e) => setDbName(e.target.value)} placeholder="mistik_dev" />
        </label>
        <label>
          <span>role de LEITURA (read-only)</span>
          <input value={dbRoleRead} onChange={(e) => setDbRoleRead(e.target.value)} placeholder="bp_agent_ro" />
        </label>
      </div>
      <div className="env-form-row">
        <label>
          <span>role de ESCRITA (DML)</span>
          <input value={dbRoleWrite} onChange={(e) => setDbRoleWrite(e.target.value)} placeholder="bp_agent_rw (deixe vazio p/ bloquear escrita)" />
        </label>
        <span className="env-form-hint" style={{ alignSelf: 'center', fontSize: 12, color: 'var(--muted)' }}>
          Acesso a banco dos agentes usa estas roles least-privilege. Configure os grants por agente no editor do agente.
        </span>
      </div>

      <label>
        <span>notas</span>
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      <label className="env-form-check">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <span>ativo</span>
      </label>

      {msg && <div className="error">{msg}</div>}

      <div className="env-form-actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'salvando…' : env ? 'salvar' : 'criar'}
        </button>
        <button type="button" className="link" onClick={onCancel}>cancelar</button>
      </div>
    </form>
  );
}
